/**
 * `/callback` — where the SSO round trip lands.
 *
 * Two obligations, both from SSO_INTEGRATION §3:
 *
 * 1. Exchange the code (the shell's `completeSsoCallback` does the PKCE half).
 * 2. **Restore the deep link.** Someone who opened `/clients` cold must sign in
 *    and arrive at `/clients`, not on the home page. `takeReturnTo` reads the
 *    path `SsoRedirect` stashed before it left.
 */

import { completeSsoCallback, takeReturnTo } from '@fiveox/shell'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import { RETURN_KEY, ssoConfig } from '../lib/sso-config'
import { session } from '../lib/session'

export function Callback() {
  const navigate = useNavigate()
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    completeSsoCallback(ssoConfig())
      .then((tokens) => {
        if (cancelled) return
        session.storeTokens(tokens)
        // `replace`, not `push`: the callback URL carries a spent code and a
        // back button must not land on it.
        // `takeReturnTo` gives null when nothing was stashed — a direct
        // sign-in rather than a deep link. That lands on the app root.
        navigate(takeReturnTo(RETURN_KEY) ?? '/', { replace: true })
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
    // Runs once. A re-render must not attempt a second exchange with a code
    // the first one already spent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      {failed ? (
        <div className="max-w-sm text-center">
          <p className="mb-4 text-sm text-foreground">Sign-in could not be completed.</p>
          <button
            type="button"
            onClick={() => navigate('/', { replace: true })}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            Try again
          </button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Signing you in…</p>
      )}
    </div>
  )
}
